import { CommonModule } from '@angular/common';
import { Component, OnChanges, OnInit, SimpleChanges, TemplateRef, computed, inject, input, output, signal, viewChild } from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { UserApi, User } from '@venecia/apis/identity';
import { ErrorMessageService, MessageService, TraceService } from '@venecia/services';

import { ResponsiveTable } from '../responsive-table/responsive-table';
import type { Column, RowAction } from '../responsive-table/responsive-table.model';
import { Spinner } from '../spinner/spinner';

@Component({
  selector: 'app-user-grid',
  standalone: true,
  imports: [CommonModule, TranslateModule, ResponsiveTable, Spinner],
  templateUrl: './user-grid.html',
  styleUrl: './user-grid.scss',
})
export class UserGrid implements OnInit, OnChanges {
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly messageService = inject(MessageService);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);
  private readonly userApi = inject(UserApi);

  readonly accountId = input<number | null>(null);
  readonly reloadVersion = input<number>(0);
  readonly detailRequested = output<User>();
  readonly totalChanged = output<number>();

  readonly loading = signal(false);
  readonly users = signal<readonly User[]>([]);

  // Pagination (server-side)
  readonly pageNumber = signal(1); // 1-based
  readonly pageSize = signal(10);
  readonly totalItems = signal(0);
  readonly statusCell = viewChild<TemplateRef<{ $implicit: User }>>('statusCell');
  readonly trackByUser = (_: number, u: User) => u.id ?? u.username ?? '';

  readonly columns = computed<readonly Column<User>[]>(() => [
    {
      id: 'avatar',
      header: '',
      value: (u) => u['avatar-url'] ?? '',
      nowrap: true,
      width: 5,
      isImage: true,
      rounded: true
    },
    {
      id: 'username',
      header: this.translateService.instant('pages.users.fields.username'),
      value: (u) => u.username ?? '',
      nowrap: true,
      width: 65
    },
    {
      id: 'active',
      header: this.translateService.instant('pages.users.fields.active'),
      cellTemplate: this.statusCell(),
      nowrap: true,
      width: 20
    },
  ]);

  readonly actions = computed<readonly RowAction<User>[]>(() => [
    {
      id: 'details',
      label: this.translateService.instant('pages.users.actions.details'),
      icon: 'fa-solid fa-pen-to-square',
    },
  ]);

  ngOnInit(): void {
    void this.loadUsers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const reloadVersionChange = changes['reloadVersion'];
    if (!reloadVersionChange || reloadVersionChange.firstChange) {
      return;
    }

    this.pageNumber.set(1);
    void this.loadUsers();
  }

  onActionSelected(ev: { actionId: string; item: User }): void {
    if (ev.actionId === 'details') {
      this.detailRequested.emit(ev.item);
    }
  }

  onPageChange(page: number): void {
    if (page === this.pageNumber()) {
      return;
    }

    this.pageNumber.set(page);
    void this.loadUsers();
  }

  private async loadUsers(): Promise<void> {
    this.loading.set(true);

    try {
      const response = await firstValueFrom(
        this.userApi.search({
          payload: this.buildSearchPayload(),
        })
      );

      this.users.set(response.body.data ?? []);
      this.totalItems.set(response.body.total ?? 0);
      this.totalChanged.emit(this.totalItems());
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.users.load-error'));
    } finally {
      this.loading.set(false);
    }
  }

  private buildSearchPayload(): Record<string, unknown> {
    const accountId = this.accountId();

    return {
      page: this.pageNumber() - 1,
      size: this.pageSize(),
      ...(accountId !== null
        ? {
          filters: [
            {
              field: 'account.id',
              value: accountId,
              comparative: 'eq',
            },
          ],
        }
        : {}),
    };
  }
}
